import React, { useMemo } from 'react';
import { Download, FileText } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  useGetAllFiles,
  type FileOrFolder,
  type NotebookFile,
} from 'components/FileManagement/hooks/useGetAllFiles';
import useDownloadFile from 'components/FileManagement/hooks/useDownloadFile';
import {
  useGetPositionsForIncidentId,
  useGetRosterForIncident,
} from 'hooks/incident/useRoster';
import { selectGroup, useAppSelector } from 'slices/commonSelectors';

import { TicketSection } from './TicketSection';
import { filesForPositions, positionNamesForTask } from './positionFolderFiles';

/**
 * Files this task inherits from folders tagged to its roster position, from
 * both the incident's files and the organization's. Read-only: the link comes
 * from where the file lives, so it is changed in the file library rather than
 * here. Renders nothing when there is nothing to show.
 */
export const PositionFilesSection: React.FC<{
  incidentId?: string;
  assigneeKind?: string | null;
  assigneeId?: string | null;
  poc?: string | null;
}> = ({ incidentId, assigneeKind, assigneeId, poc }) => {
  const currentGroup = useAppSelector(selectGroup);
  const groupGuid: string = currentGroup?.group_guid ?? '';

  const { data: positions = [] } = useGetPositionsForIncidentId(
    incidentId ?? ''
  );
  const { data: rosterEntries = [] } = useGetRosterForIncident(incidentId);
  const { data: incidentFiles } = useGetAllFiles(incidentId);
  const { data: orgFiles } = useGetAllFiles(groupGuid, 'organization');

  const download = useDownloadFile();

  const positionNames = useMemo(
    () =>
      positionNamesForTask(
        { assignee_kind: assigneeKind, assignee_id: assigneeId, poc },
        positions,
        rosterEntries
      ),
    [assigneeKind, assigneeId, poc, positions, rosterEntries]
  );

  const items = useMemo<FileOrFolder[]>(
    () => [...(incidentFiles?.dataset ?? []), ...(orgFiles?.dataset ?? [])],
    [incidentFiles, orgFiles]
  );

  const files = useMemo(
    () => filesForPositions(items, positionNames),
    [items, positionNames]
  );

  const byId = useMemo(
    () => new Map(items.map((item) => [item.id, item])),
    [items]
  );

  if (files.length === 0) return null;

  return (
    <TicketSection
      title="Position Files"
      count={files.length}
      description="From folders tagged to this task's roster position. Manage them in Files."
    >
      <ul className="tw:m-0 tw:list-none tw:space-y-1 tw:p-0">
        {files.map((file) => {
          const source = byId.get(file.id);
          return (
            <li
              key={file.id}
              className="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:rounded-md tw:border tw:border-border tw:px-2 tw:py-1.5"
            >
              <span className="tw:flex tw:min-w-0 tw:items-center tw:gap-2">
                <FileText className="tw:h-4 tw:w-4 tw:shrink-0 tw:text-muted-foreground" />
                <span className="tw:truncate tw:text-sm">{file.name}</span>
                <span className="tw:shrink-0 tw:text-[11px] tw:text-muted-foreground">
                  {file.positionName}
                </span>
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={`Download ${file.name}`}
                onClick={() => {
                  if (source?.type === 'file') {
                    void download(source as NotebookFile);
                  }
                }}
              >
                <Download className="tw:h-4 tw:w-4" />
              </Button>
            </li>
          );
        })}
      </ul>
    </TicketSection>
  );
};

export default PositionFilesSection;
